import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { IconButton, InputLabel, OutlinedInput, Tab, Typography, InputAdornment } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { add } from 'date-fns';
import { useHistory } from 'react-router-dom'; 
import { toast } from 'react-toastify';
import { useStore } from '@stores/rootStoreContext';
import { defaultFilters } from '@stores/storiesListStore/storiesListStore.defaultValues';
import StoriesListContent from './StoriesListContent/StoriesListContent';
import {
    ButtonsWrapper,
    ButtonWrapper,
    DatePickersWrapper,
    DatePickerWrapper,
    FiltersFieldsWrapper,
    KeyboardDatePickerWrapper,
    SearchControlWrapper,
    SearchWrapper,
    StyledTabs,
} from './StoriesList.styles';
import { StoriesListProps } from './StoriesListProps';
import { STORIES_LIST_CONSTS } from './StoriesList.consts';
import { useCreateNewStoryMutation } from '../StoryEditor/gql/StoryEditor.generated'; 

const StoriesList: FC<StoriesListProps> = ({ tabNames }) => {
    const [value, setValue] = useState(0);
    const { storiesListStore } = useStore();
    const { filters, setFiltersWithPrevState } = storiesListStore;
    const [searchString, setSearchString] = useState<string | null>(filters.searchString);
    const [createdAtFrom, setCreatedAtFrom] = useState<Date | null>(filters.createdAtFrom);
    const [createdAtTo, setCreatedAtTo] = useState<Date | null>(filters.createdAtTo);

    const [maxDate] = useState(add(new Date(), { days: 1 }));
    const [minDate] = useState(new Date(2006, 1, 1));

    const [createdAtFromMaxDate, setCreatedAtFromMaxDate] = useState<Date | null>(
        add(filters.createdAtTo || maxDate, { days: -1 })
    );
    const [createdAtToMinDate, setCreatedAtFromMinDate] = useState<Date | null>(
        add(filters.createdAtFrom || minDate, { days: 1 })
    );

    const history = useHistory(); 
    const [createNewStoryMutation, { loading: createLoading }] = useCreateNewStoryMutation();

    const changeAtFrom = useCallback(
        (date: unknown) => {
            const dateVal = date instanceof Date ? date : null;
            setFiltersWithPrevState((prev) => ({ ...prev, createdAtFrom: dateVal }));
            setCreatedAtFrom(dateVal);
            setCreatedAtFromMinDate(add(dateVal || minDate, { days: 1 }));
        },
        [setFiltersWithPrevState, minDate]
    );

    const changeAtTo = useCallback(
        (date: unknown) => {
            const dateVal = date instanceof Date ? date : null;
            setFiltersWithPrevState((prev) => ({ ...prev, createdAtTo: dateVal }));
            setCreatedAtTo(dateVal);
            setCreatedAtFromMaxDate(add(dateVal || maxDate, { days: -1 }));
        },
        [setFiltersWithPrevState, maxDate]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setFiltersWithPrevState((prev) => ({ ...prev, searchString }));
        }, 500);

        return () => clearTimeout(timer);
    }, [searchString, setFiltersWithPrevState]);

    const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const clearFilters = useCallback(() => {
        changeAtFrom(defaultFilters.createdAtFrom);
        changeAtTo(defaultFilters.createdAtTo);
        setSearchString(defaultFilters.searchString);
    }, [changeAtFrom, changeAtTo]);

    const handleCreateStory = useCallback(async () => {
        try {
            const createNewStoryMutationData = {
            };

            const result = await createNewStoryMutation({
                variables: {
                    data: createNewStoryMutationData,
                },
            });

            const storyUid = result.data?.createStory?.story?.uid;
            if (storyUid) {
                toast.success(STORIES_LIST_CONSTS.storyCreated, {
                    autoClose: 1000,
                    hideProgressBar: true,
                    containerId: 'success',
                });
                history.push(`/story/editor/${storyUid}`);
            } else {
                throw new Error('No UID returned from createStory mutation');
            }
        } catch (e) {
            console.error('Error creating story:', e);
            toast.error(STORIES_LIST_CONSTS.errorCreatingStory, {
                hideProgressBar: true,
                containerId: 'error',
            });
        }
    }, [createNewStoryMutation, history]);

    return (
        <>
            <StyledTabs value={value} onChange={handleChange} indicatorColor="primary" textColor="primary">
                {tabNames.map((tab, i) => (
                    <Tab label={tab.title} key={i} />
                ))}
            </StyledTabs>
            <SearchWrapper>
                <FiltersFieldsWrapper>
                    <DatePickersWrapper>
                        <DatePickerWrapper>
                            <KeyboardDatePickerWrapper
                                format="yyyy-MM-dd"
                                minDate={minDate}
                                maxDate={createdAtFromMaxDate}
                                label={STORIES_LIST_CONSTS.createDateFrom}
                                value={createdAtFrom}
                                onChange={changeAtFrom}
                                slotProps={{
                                    openPickerButton: { color: 'primary' },
                                    field: { clearable: true, id: 'date-picker-from' },
                                    toolbar: { hidden: true },
                                }}
                            />
                        </DatePickerWrapper>
                        <DatePickerWrapper>
                            <KeyboardDatePickerWrapper
                                format="yyyy-MM-dd"
                                label={STORIES_LIST_CONSTS.createDateTo}
                                value={createdAtTo}
                                onChange={changeAtTo}
                                minDate={createdAtToMinDate}
                                maxDate={maxDate}
                                slotProps={{
                                    openPickerButton: { color: 'primary' },
                                    field: { clearable: true, id: 'date-picker-to' },
                                    toolbar: { hidden: true },
                                }}
                            />
                        </DatePickerWrapper>
                    </DatePickersWrapper>
                    <SearchControlWrapper>
                        <InputLabel
                            htmlFor="stories-filter-search"
                            style={{
                                width: '70%',
                            }}
                        >
                            {STORIES_LIST_CONSTS.searchByTitle}
                        </InputLabel>
                        <OutlinedInput
                            id="stories-filter-search"
                            value={searchString}
                            label={STORIES_LIST_CONSTS.searchByTitle}
                            onChange={(e) => setSearchString(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '42px',
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    {searchString && (
                                        <IconButton edge="end" size="small" onClick={() => setSearchString('')}>
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            }
                        />
                    </SearchControlWrapper>
                </FiltersFieldsWrapper>
                <ButtonsWrapper>
                    <ButtonWrapper
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleCreateStory}
                        disabled={createLoading}
                    >
                        <Typography variant="text14M">{STORIES_LIST_CONSTS.create}</Typography>
                    </ButtonWrapper>
                    <ButtonWrapper variant="outlined" color="primary" size="small" onClick={clearFilters}>
                        <Typography variant="text14M">{STORIES_LIST_CONSTS.clearFilters}</Typography>
                    </ButtonWrapper>
                </ButtonsWrapper>
            </SearchWrapper>

            {tabNames.map((tab, i) => (
                <div key={i} hidden={value !== i}>
                    {value === i && <StoriesListContent status={tab?.name} />}
                </div>
            ))}
        </>
    );
};

export default observer(StoriesList);